import React from 'react';
import { ArrowUpRight, Check, X } from 'lucide-react';

// A leader's field guide after the HBR Classic (reprint 99609): Oncken & Wass,
// "Management Time: Who's Got the Monkey?" (1974), reissued 1999 with Stephen
// R. Covey's commentary. Terminology is updated for the modern workplace; the
// original frames ownership as "a monkey on your back". Quoted lines are from
// the reprint. Diagram proportions are illustrative.

const NAV = [
  { href: '#time', label: 'Time' },
  { href: '#transfer', label: 'Transfer' },
  { href: '#spectrum', label: 'Spectrum' },
  { href: '#playbook', label: 'Playbook' },
  { href: '#rhythm', label: 'Rhythm' },
  { href: '#lens', label: 'Lens' },
  { href: '#cheatsheet', label: 'Cheat Sheet' },
];

// Emphasis palette for the time bars. Grays are deliberate context; the story
// is rust (time absorbed from your team) converting to navy (discretionary).
// Rust/navy validated: CVD ΔE 67.8, both ≥3:1 on the paper surface.
const TIME_SEGMENTS = {
  manager: { color: '#3f4854', label: 'Manager-directed', note: 'set by your manager. Not negotiable' },
  org: { color: '#9aa1ab', label: 'Org-directed', note: 'requests from peers and the wider organization. Also not negotiable' },
  absorbed: { color: '#b23a32', label: 'Absorbed from your team', note: 'problems you’ve taken over from your people. The only slice you can shrink' },
  discretionary: { color: '#1c5cab', label: 'Discretionary', note: 'the part that’s actually yours' },
};

const TIME_BARS = [
  {
    label: 'A typical week',
    slices: [
      { key: 'manager', pct: 25 },
      { key: 'org', pct: 25 },
      { key: 'absorbed', pct: 40 },
      { key: 'discretionary', pct: 10 },
    ],
  },
  {
    label: 'After you hand ownership back',
    slices: [
      { key: 'manager', pct: 25 },
      { key: 'org', pct: 25 },
      { key: 'absorbed', pct: 10 },
      { key: 'discretionary', pct: 40 },
    ],
  },
];

const SCENES = [
  {
    title: 'The hallway',
    them: 'Got a minute? We’ve got a problem…',
    you: 'Let me think about it and get back to you.',
    why: 'You now owe the next move. Tomorrow your report will check in on your progress. Notice who’s supervising who.',
  },
  {
    title: 'The status update',
    them: 'Here’s where we landed today.',
    you: 'Fine. Send me a write-up.',
    why: 'The write-up lands in your inbox, and the next move is yours again. Sit on it, and the follow-ups start.',
  },
  {
    title: 'The open offer',
    them: 'I’ll get started on the proposal.',
    you: 'Just let me know how I can help.',
    why: 'They can’t proceed until you weigh in, and the draft waits in your queue for weeks. Your desk becomes the bottleneck.',
  },
  {
    title: 'The kickoff',
    them: 'Ready to scope the new role.',
    you: 'I’ll put together a first draft for us to discuss.',
    why: 'They hold the role and the responsibility; you hold the next move. Until you make it, they’re stalled.',
  },
];

const ZONES = {
  refuse: { tag: 'Don’t accept', dot: 'bg-rust', border: 'border-rust', text: 'text-rust-deep' },
  minimum: { tag: 'The minimum', dot: 'bg-ink-faint', border: 'border-ink-faint', text: 'text-ink-muted' },
  standard: { tag: 'The standard', dot: 'bg-navy', border: 'border-navy', text: 'text-navy-deep' },
};

const DEGREES = [
  { n: 1, text: 'Wait until told', zone: 'refuse', timing: false, content: false },
  { n: 2, text: 'Ask what to do', zone: 'refuse', timing: true, content: false },
  { n: 3, text: 'Recommend, then take resulting action', zone: 'minimum', timing: true, content: true },
  { n: 4, text: 'Act, but advise at once', zone: 'standard', timing: true, content: true },
  { n: 5, text: 'Act on own, then routinely report', zone: 'standard', timing: true, content: true },
];

// Zone bracket row under the spectrum: col spans must sum to the 5 degrees.
const ZONE_BRACKETS = [
  { zone: 'refuse', span: 'col-span-2' },
  { zone: 'minimum', span: 'col-span-1' },
  { zone: 'standard', span: 'col-span-2' },
];

const PLAYBOOK = [
  {
    title: 'Keep ownership where it lives',
    detail:
      'Help with the problem without taking it over. The essay is blunt about this: “The instant your problem becomes mine, you no longer have a problem.”',
  },
  {
    title: 'Agree the next move and who owns it',
    detail:
      'Level 3 or above: they recommend or act; you advise. If no one can see the next move yet, it stays with them overnight and comes back at a set time.',
  },
  {
    title: 'Book the follow-up before they leave',
    detail:
      'Five to fifteen minutes on your calendar, live. Email hands the next move straight back to you.',
  },
  {
    title: 'They leave owning it',
    detail:
      'Now they owe you the move, and you get to be the one asking how it’s going. At the follow-up, the item either advances or you close it out.',
  },
];

const RHYTHM_RULES = [
  {
    title: 'Move it or close it',
    detail: 'Every open item gets a decision at each review. It advances, or you end it on purpose. Nothing sits and stalls.',
  },
  {
    title: 'Cap your open items',
    detail: 'Your team can only keep as many problems moving as you have time to review. Stay under that number.',
  },
  {
    title: 'Review on schedule',
    detail: 'Set check-ins. Don’t chase status ad hoc, and don’t wait for something to escalate.',
  },
  {
    title: 'Review in conversation, not email',
    detail: 'In writing, the next move boomerangs to you. Documents support the discussion; they never replace it.',
  },
  {
    title: 'Give every item a next check-in and an initiative level',
    detail: 'Adjust both by agreement, but never let either go vague. Vague items stall, or land back on you.',
  },
];

const LENS_CHECKS = [
  {
    title: 'Readiness first',
    detail:
      'Handing a problem back works only when the person is willing and able to run with it. Sometimes you have to develop the person before you can hand anything back. That costs time this week and repays it every week after.',
  },
  {
    title: 'Trust over instruction',
    detail:
      '“Hand it back” alone is too authoritarian. Keep a running dialogue going, because people who are afraid to fail in front of you will keep coming back for answers.',
  },
  {
    title: 'Systems that reward it',
    detail:
      'Delegation sticks when the organization rewards managers for growing decision-makers rather than for having every answer.',
  },
  {
    title: 'Your own habits',
    detail:
      'Many managers hold on because letting go feels like losing standing. In practice, a report who runs with things makes you look better.',
  },
];

const RETIRE = [
  'Let me think about it and get back to you.',
  'Send me a write-up on that.',
  'Just let me know how I can help.',
  'I’ll put together a first draft for us to discuss.',
];

const REPLACE = [
  'What do you recommend?',
  'Take the next step, and flag me as soon as you’ve moved.',
  'Run with it. We’ll cover it at our regular check-in.',
  'Bring me your draft Thursday at ten; we’ll take fifteen minutes.',
];

const Kicker = ({ children }) => (
  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted">
    {children}
  </p>
);

const Section = ({ id, number, kicker, title, lede, children }) => (
  <section id={id} aria-labelledby={`${id}-heading`} className="scroll-mt-16 border-t border-rule">
    <div className="mx-auto grid max-w-6xl gap-x-12 gap-y-8 px-6 py-16 sm:py-20 lg:grid-cols-[180px_1fr]">
      <div className="lg:pt-1">
        <p className="font-serif text-6xl leading-none text-ink/10" aria-hidden="true">
          {number}
        </p>
        <div className="mt-3">
          <Kicker>{kicker}</Kicker>
        </div>
      </div>
      <div>
        <h2 id={`${id}-heading`} className="max-w-2xl font-serif text-3xl font-medium leading-tight text-ink sm:text-4xl">
          {title}
        </h2>
        {lede && <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-muted">{lede}</p>}
        {children}
      </div>
    </div>
  </section>
);

const ControlMark = ({ label, has }) => (
  <span className={`inline-flex items-center gap-1 ${has ? 'text-ink-muted' : 'text-ink-faint'}`}>
    {has ? <Check size={11} aria-hidden="true" /> : <X size={11} aria-hidden="true" />}
    {label}
  </span>
);

const TimeBar = ({ bar }) => (
  <div>
    <p className="mb-2 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-ink-muted">
      {bar.label}
    </p>
    <div
      className="flex h-9 w-full gap-[2px]"
      role="img"
      aria-label={`${bar.label}: ${bar.slices.map((s) => `${TIME_SEGMENTS[s.key].label} about ${s.pct}%`).join(', ')} (illustrative)`}
    >
      {bar.slices.map((slice) => (
        <div key={slice.key} style={{ width: `${slice.pct}%`, backgroundColor: TIME_SEGMENTS[slice.key].color }} />
      ))}
    </div>
  </div>
);

export default function App() {
  return (
    <div className="min-h-screen">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-ink focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-rule bg-paper/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3.5">
          <a
            href="#top"
            className="shrink-0 whitespace-nowrap font-mono text-xs font-semibold uppercase tracking-[0.2em] text-ink"
          >
            Initiative Spectrum
          </a>
          <nav aria-label="Sections" className="-mx-2 overflow-x-auto">
            <ul className="flex items-center gap-5 whitespace-nowrap px-2 font-mono text-[11px] uppercase tracking-[0.15em]">
              {NAV.map((item, index) => (
                <li key={item.href}>
                  <a href={item.href} className="text-ink-muted transition-colors hover:text-ink">
                    <span className="text-ink-faint">{String(index + 1).padStart(2, '0')}</span>{' '}
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      <main id="main">
        {/* Hero */}
        <div id="top" className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:pb-24 sm:pt-20">
          <Kicker>
            A field guide for leaders · After Oncken &amp; Wass (HBR, 1974) and Covey (1999)
          </Kicker>
          <h1 className="mt-6 max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink sm:text-7xl">
            Why do managers run out of time while their teams run out of work?
          </h1>
          <div className="mt-10 max-w-2xl">
            <p className="text-lg leading-relaxed text-ink-muted">
                The most-reprinted essay in <em>Harvard Business Review</em> history answered that
                question fifty years ago: managers keep taking ownership of their team’s problems,
                one “next move” at a time. This guide translates that essay, plus Stephen R.
                Covey’s commentary on it, into the language we use at work today. It takes about
                five minutes.
              </p>
              <div className="mt-8 border-l-2 border-navy pl-6">
                <p className="font-serif text-2xl leading-snug text-ink">
                  Every open problem has a next move. Whoever owns the next move owns the
                  problem, no matter whose name is on it.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                  The original pictured this as a monkey leaping onto the manager’s back. We’ll
                  call it what your team calls it today: <strong className="text-ink">ownership</strong>.
                </p>
              </div>
          </div>
        </div>

        <Section
          id="time"
          number="01"
          kicker="The diagnosis"
          title="Only one slice of your week is yours"
          lede="The essay starts with an inventory: a manager’s time has three sources, and only part of one is discretionary."
        >
          <div className="mt-10 space-y-7 border border-rule bg-white p-6 sm:p-8">
            {TIME_BARS.map((bar) => (
              <TimeBar key={bar.label} bar={bar} />
            ))}
            <ul className="divide-y divide-rule border-t border-rule">
              {Object.entries(TIME_SEGMENTS).map(([key, seg]) => (
                <li key={key} className="flex items-baseline gap-3 py-2.5 text-sm">
                  <span className="h-2.5 w-2.5 shrink-0 self-center" style={{ backgroundColor: seg.color }} aria-hidden="true" />
                  <span className="shrink-0 font-medium text-ink">{seg.label}</span>
                  <span className="text-ink-muted">{seg.note}</span>
                </li>
              ))}
            </ul>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
              Proportions illustrative
            </p>
          </div>
          <div className="mt-8 max-w-2xl border-l-2 border-navy pl-6">
            <Kicker>The lever</Kicker>
            <p className="mt-2 text-lg leading-relaxed text-ink">
              You can’t refuse what your manager and the organization require. What you can do is
              hand problems back. Every one you return converts absorbed time into discretionary
              time, and discretionary time is how you get ahead of everything else.
            </p>
          </div>
        </Section>

        <Section
          id="transfer"
          number="02"
          kicker="The mechanism"
          title="Ownership moves on well-meant phrases"
          lede="Four scenes from the essay. In each one, a manager tries to help and walks away owning the next move."
        >
          <div className="mt-10 grid gap-px border border-rule bg-rule md:grid-cols-2">
            {SCENES.map((scene, index) => (
              <article key={scene.title} className="bg-white p-6">
                <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
                  Scene {index + 1} · {scene.title}
                </h3>
                <dl className="mt-5 space-y-4">
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                      Your report
                    </dt>
                    <dd className="mt-1 text-sm text-ink-muted">“{scene.them}”</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.15em] text-navy">
                      You
                    </dt>
                    <dd className="mt-1 font-serif text-xl italic leading-snug text-ink">
                      “{scene.you}”
                    </dd>
                  </div>
                </dl>
                <p className="mt-5 border-t border-rule pt-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-rust">
                  → Ownership just moved to you
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{scene.why}</p>
              </article>
            ))}
          </div>
          <div className="mt-10 flex flex-col gap-6 border-y border-rule py-8 sm:flex-row sm:items-center sm:gap-10">
            <p className="shrink-0 font-serif text-7xl font-medium leading-none text-ink sm:text-8xl">
              60
            </p>
            <p className="max-w-xl text-base leading-relaxed text-ink-muted">
              Four direct reports, three handoffs a day, five days: sixty open items you own by
              Friday. In the essay, the overwhelmed manager comes in on Saturday to catch up,
              looks out the window, and sees his whole team on the golf course.{' '}
              <em className="font-serif text-lg text-ink">
                “The more he gets caught up, the more he will fall behind.”
              </em>
            </p>
          </div>
        </Section>

        <Section
          id="spectrum"
          number="03"
          kicker="The namesake"
          title="The five degrees of initiative"
          lede="Anyone can operate at five levels of initiative. Rule out the bottom two: for your team toward you, and for yourself toward your own leadership."
        >
          {/* Desktop: horizontal scale. The dots sit on a continuous axis line. */}
          <div className="mt-12 hidden md:block">
            <div className="grid grid-cols-5">
              {DEGREES.map((degree, index) => {
                const zone = ZONES[degree.zone];
                return (
                  <div key={degree.n} className="relative h-4">
                    <span
                      className={`absolute top-1/2 h-px -translate-y-1/2 bg-rule ${
                        index === 0 ? 'left-1/2 right-0' : index === DEGREES.length - 1 ? 'left-0 right-1/2' : 'inset-x-0'
                      }`}
                      aria-hidden="true"
                    />
                    <span
                      className={`absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ${zone.dot}`}
                      aria-hidden="true"
                    />
                  </div>
                );
              })}
            </div>
            <ol className="mt-4 grid grid-cols-5 gap-4">
              {DEGREES.map((degree) => (
                <li key={degree.n} className="px-1 text-center">
                  <p className="font-mono text-[11px] text-ink-faint">{degree.n}</p>
                  <p className="mt-1 font-serif text-lg font-medium leading-tight text-ink">
                    {degree.text}
                  </p>
                  <p className="mt-2 flex justify-center gap-3 font-mono text-[10px] uppercase tracking-[0.1em]">
                    <ControlMark label="timing" has={degree.timing} />
                    <ControlMark label="content" has={degree.content} />
                  </p>
                </li>
              ))}
            </ol>
            <div className="mt-6 grid grid-cols-5 gap-x-4">
              {ZONE_BRACKETS.map((bracket) => {
                const zone = ZONES[bracket.zone];
                return (
                  <p
                    key={bracket.zone}
                    className={`${bracket.span} border-t-2 pt-2 text-center font-mono text-[11px] font-semibold uppercase tracking-[0.2em] ${zone.border} ${zone.text}`}
                  >
                    {zone.tag}
                  </p>
                );
              })}
            </div>
          </div>
          {/* Mobile: vertical list */}
          <ol className="mt-10 divide-y divide-rule border-y border-rule md:hidden">
            {DEGREES.map((degree) => {
              const zone = ZONES[degree.zone];
              return (
                <li key={degree.n} className="flex items-start gap-4 py-4">
                  <span className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${zone.dot}`} aria-hidden="true" />
                  <div>
                    <p className="font-serif text-lg font-medium leading-tight text-ink">
                      <span className="font-mono text-xs text-ink-faint">{degree.n} · </span>
                      {degree.text}
                    </p>
                    <p className={`mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${zone.text}`}>
                      {zone.tag}
                    </p>
                    <p className="mt-1.5 flex gap-3 font-mono text-[10px] uppercase tracking-[0.1em]">
                      <ControlMark label="timing" has={degree.timing} />
                      <ControlMark label="content" has={degree.content} />
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="mt-10 grid gap-px border border-rule bg-rule sm:grid-cols-2">
            <div className="bg-white p-6">
              <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted">
                With your team
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                Don’t take questions at levels 1 and 2; ask for a recommendation instead. Every
                problem should leave your office with an agreed level (3 or higher) and a
                follow-up on the calendar.
              </p>
            </div>
            <div className="bg-white p-6">
              <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted">
                With your own leadership
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-muted">
                The same scale runs upward. At levels 1 and 2 you control neither the timing nor
                the content of your own work; from 3 up you control both, with the most control
                at 5.
              </p>
            </div>
          </div>
        </Section>

        <Section
          id="playbook"
          number="04"
          kicker="The move"
          title="When a problem lands on your desk"
          lede="The whole method is one conversation, run the same way every time."
        >
          <ol className="mt-10 max-w-3xl divide-y divide-rule border-y border-rule">
            {PLAYBOOK.map((step, index) => (
              <li key={step.title} className="grid gap-2 py-6 sm:grid-cols-[110px_1fr] sm:gap-6">
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy">
                  Step {index + 1}
                </p>
                <div>
                  <h3 className="font-serif text-xl font-medium text-ink">{step.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section
          id="rhythm"
          number="05"
          kicker="The operating rhythm"
          title="Five rules for every item you’re coaching"
          lede="The essay’s five “hard-and-fast rules,” translated. Break one, and it costs you discretionary time."
        >
          <ol className="mt-10 divide-y divide-rule border-y border-rule">
            {RHYTHM_RULES.map((rule, index) => (
              <li key={rule.title} className="grid gap-2 py-6 sm:grid-cols-[110px_1fr] sm:gap-6">
                <p className="font-serif text-3xl leading-none text-ink/15" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </p>
                <div>
                  <h3 className="font-serif text-xl font-medium text-ink">{rule.title}</h3>
                  <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink-muted">{rule.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section
          id="lens"
          number="06"
          kicker="The modern lens"
          title="Covey: hand it back with care"
          lede="Covey’s 1999 commentary endorses the diagnosis and adds the human side: handing problems back only works inside a real relationship. The payoff is time for the work that’s important but never urgent."
        >
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted">
                Where the reclaimed time goes
              </h3>
              <div className="grid grid-cols-[auto_1fr_1fr] text-sm">
                <div />
                <p className="border-b border-rule px-3 pb-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                  Urgent
                </p>
                <p className="border-b border-l border-rule px-3 pb-2 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint">
                  Not urgent
                </p>
                <p className="flex items-center border-rule pr-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint [writing-mode:vertical-rl] rotate-180">
                  Important
                </p>
                <div className="border-rule p-4">
                  <p className="font-serif text-lg font-medium text-ink">Crises</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                    Handle them, then ask why they keep happening.
                  </p>
                </div>
                <div className="border-l border-rule bg-navy-wash p-4">
                  <p className="font-serif text-lg font-medium text-navy-deep">High-impact work</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                    Strategy and developing people. This is what the reclaimed time is for.
                  </p>
                </div>
                <p className="flex items-center border-t border-rule pr-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-faint [writing-mode:vertical-rl] rotate-180">
                  Not important
                </p>
                <div className="border-t border-rule bg-rust-wash p-4">
                  <p className="font-serif text-lg font-medium text-rust-deep">
                    Your team’s open items
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                    Covey heard it from executives over and over: half their time went here.
                  </p>
                </div>
                <div className="border-l border-t border-rule p-4">
                  <p className="font-serif text-lg font-medium text-ink">Noise</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink-muted">Decline it.</p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-muted">
                Before you hand one back
              </h3>
              <ul className="divide-y divide-rule border-y border-rule">
                {LENS_CHECKS.map((check) => (
                  <li key={check.title} className="py-4">
                    <h4 className="font-serif text-lg font-medium text-ink">{check.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-ink-muted">{check.detail}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        <Section
          id="cheatsheet"
          number="07"
          kicker="Monday morning"
          title="Say this, not that"
          lede="Four phrases that hand you ownership without you noticing, and what to say instead."
        >
          <div className="mt-10 grid gap-px border border-rule bg-rule md:grid-cols-2">
            <div className="bg-white p-6 sm:p-8">
              <h3 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-rust">
                <X size={14} aria-hidden="true" /> Retire these
              </h3>
              <ul className="mt-5 divide-y divide-rule">
                {RETIRE.map((phrase) => (
                  <li key={phrase} className="py-3.5 font-serif text-lg italic leading-snug text-ink-muted">
                    “{phrase}”
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6 sm:p-8">
              <h3 className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy">
                <Check size={14} aria-hidden="true" /> Use these
              </h3>
              <ul className="mt-5 divide-y divide-rule">
                {REPLACE.map((phrase) => (
                  <li key={phrase} className="py-3.5 font-serif text-lg leading-snug text-ink">
                    “{phrase}”
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mx-auto mt-12 max-w-2xl text-center font-serif text-2xl leading-snug text-ink">
            Run this consistently and, as the essay promises, the value of every hour you spend
            managing your time “multiplies without theoretical limit.”
          </p>
          <p className="mx-auto mt-6 max-w-2xl text-center text-lg text-ink-muted">
            You’ve got real opportunity in front of you and the right people to go after it.
            Let’s go get it.
          </p>
        </Section>
      </main>

      <footer className="border-t border-rule bg-ink text-white/70">
        <div className="mx-auto grid max-w-6xl gap-x-12 gap-y-6 px-6 py-12 text-sm leading-relaxed lg:grid-cols-[180px_1fr]">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
            Sources
          </p>
          <div className="max-w-2xl space-y-3">
            <p>
              Oncken, W., Jr., &amp; Wass, D. L. (1974). “Management Time: Who’s Got the Monkey?”{' '}
              <em>Harvard Business Review</em>; reissued as an HBR Classic in the
              November/December 1999 issue with Stephen R. Covey’s commentary, “Making Time for
              Gorillas.”
            </p>
            <p>
              Terminology on this page is updated for the modern workplace; quoted lines come
              straight from the reprint. The original is short and worth your time:{' '}
              <a
                href="https://hbr.org/1999/11/management-time-whos-got-the-monkey"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 font-medium text-white underline decoration-white/30 underline-offset-4 hover:decoration-white"
              >
                read it at hbr.org
                <ArrowUpRight size={14} aria-hidden="true" />
              </a>
              .
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
